/**
 * 🔴 F14/D197 — a ordem do cardápio, num lugar só.
 *
 * Mora em `shared/common` porque a regra tem **dois** consumidores: o cardápio do hóspede
 * (`MenuService`) e as listas do painel, onde o gerente digita a posição. As duas telas discordarem
 * seria pior que não ter ordenação — ele arruma numa e confere na outra. Mesmo argumento que trouxe
 * o `normalizeOrderPayload` para cá.
 *
 * A regra é: **quem tem posição vem primeiro, em ordem crescente; quem não tem vai para o fim, em
 * ordem alfabética.** O desempate é SEMPRE o nome, inclusive entre dois com a mesma posição.
 *
 * O "nulo por último" não é detalhe de implementação, é o que torna a funcionalidade adotável: um
 * estabelecimento com 30 grupos não vai numerar os 30 antes de ver resultado. Ele numera "Entradas,
 * Pratos, Bebidas, Sobremesas" e o resto continua exatamente onde estava — alfabético, atrás. Se o
 * nulo virasse zero, numerar um grupo jogaria os outros 29 para a frente dele de uma vez.
 *
 * `localeCompare` e não `<`: "Água" tem de vir antes de "Bebidas", e a comparação de code point põe
 * todo acento depois de `z`.
 */
export function compareByMenuOrder<T extends { order?: number | null; name: string }>(left: T, right: T): number {
  const leftHasOrder = typeof left.order === 'number';
  const rightHasOrder = typeof right.order === 'number';

  if (leftHasOrder && rightHasOrder && left.order !== right.order) {
    return (left.order as number) - (right.order as number);
  }
  if (leftHasOrder !== rightHasOrder) {
    return leftHasOrder ? -1 : 1;
  }

  return left.name.localeCompare(right.name);
}

/** Cópia ordenada — nunca ordena no lugar: o array vem do cache do catálogo, compartilhado. */
export function sortByMenuOrder<T extends { order?: number | null; name: string }>(items: readonly T[]): T[] {
  return [...items].sort(compareByMenuOrder);
}
